/**
 * Button — atom (CES standard)
 * Replicates the client portal's Button: rounded rectangle, semibold,
 * orange primary. Extra variants (danger/success) and the `small` size
 * cover the module's compact actions.
 */

import type { ButtonHTMLAttributes } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  small?: boolean;
}

const VARIANT_CLASSES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary: 'bg-orange-500 text-white hover:bg-orange-600 active:bg-orange-700',
  secondary: 'bg-gray-200 text-gray-800 hover:bg-gray-300 active:bg-gray-400',
  danger: 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700',
  success: 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700',
};

export function Button({ variant = 'primary', small = false, className, ...rest }: ButtonProps) {
  const classes = [
    'rounded font-semibold focus:outline-none transition-all duration-200 cursor-pointer',
    small ? 'px-3 py-1 text-sm' : 'px-4 py-2',
    VARIANT_CLASSES[variant],
    'disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:opacity-60',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');
  return <button type="button" className={classes} {...rest} />;
}
